import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import { Box, Button, ButtonBase, Checkbox, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { filter, join, map, times } from 'es-toolkit/compat';
import Markdown from 'markdown-to-jsx';
import React from 'react';

import { FollowUpOption } from 'interfaces/agentSession';
import { useEntityMergeTagResolver } from 'utils/useEntityMergeTagResolver';
import { markdownContentSx, markdownOverrides } from './style';

const STREAMING_DOT_COUNT = 3;
const DEFAULT_STREAMING_LABEL = 'Thinking';

const buildSuggestedPrompt = (selectedOptions: FollowUpOption[]): string => {
  const usableOptions = filter(selectedOptions, (option) => Boolean(option.prompt?.trim()));

  if (usableOptions.length === 0) {
    return '';
  }

  if (usableOptions.length === 1) {
    return usableOptions[0].prompt.trim();
  }

  return join(
    [
      'Please continue with all of the following next steps:',
      ...map(usableOptions, (option, index) => `${index + 1}. ${option.prompt.trim()}`),
    ],
    '\n'
  );
};

const StreamingResponseIndicator: React.FC<{ compact?: boolean; label?: string }> = ({
  compact = false,
  label = DEFAULT_STREAMING_LABEL,
}) => {
  const theme = useTheme();

  return (
    <Stack
      component="span"
      data-testid="assistant-streaming-indicator"
      direction="row"
      spacing={0.75}
      alignItems="center"
      role={compact ? undefined : 'status'}
      aria-label={compact ? undefined : label}
      aria-hidden={compact || undefined}
      sx={{
        display: 'inline-flex',
        width: 'fit-content',
        maxWidth: '100%',
        mt: compact ? 0 : 0.75,
        ml: compact ? 0.5 : 0,
        px: compact ? 0.75 : 1,
        py: compact ? 0.35 : 0.55,
        borderRadius: 999,
        border: `1px solid ${alpha(theme.palette.primary.main, compact ? 0.14 : 0.18)}`,
        bgcolor: alpha(theme.palette.primary.main, compact ? 0.06 : 0.08),
        boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, theme.palette.mode === 'light' ? 0.3 : 0.08)}`,
        verticalAlign: 'middle',
      }}
    >
      {!compact ? (
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}
        >
          {label}
        </Typography>
      ) : null}
      <Stack component="span" direction="row" spacing={0.35} alignItems="center">
        {times(STREAMING_DOT_COUNT, (index) => (
          <Box
            key={index}
            component="span"
            sx={{
              width: compact ? 5 : 6,
              height: compact ? 5 : 6,
              borderRadius: '50%',
              bgcolor: index === 1 ? 'primary.main' : 'text.secondary',
              opacity: index === 1 ? 0.95 : 0.6,
              animation: 'copilotStreamingDot 1.2s ease-in-out infinite',
              animationDelay: `${index * 0.16}s`,
              '@keyframes copilotStreamingDot': {
                '0%, 80%, 100%': { opacity: 0.35, transform: 'translateY(0) scale(0.92)' },
                '40%': { opacity: 1, transform: 'translateY(-2px) scale(1)' },
              },
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                transform: 'none',
                opacity: 0.9,
              },
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
};

const FollowUpOptionsPanel: React.FC<{
  followUpOptions: FollowUpOption[];
  onUsePromptSuggestion?: (prompt: string) => void;
  onSendPromptSuggestion?: (prompt: string) => void;
  disabled?: boolean;
}> = ({ followUpOptions, onUsePromptSuggestion, onSendPromptSuggestion, disabled = false }) => {
  const theme = useTheme();
  const [selectedIndexes, setSelectedIndexes] = React.useState<number[]>([]);

  const selectedOptions = React.useMemo(
    () =>
      map(selectedIndexes, (index) => followUpOptions[index]).filter((option): option is FollowUpOption =>
        Boolean(option)
      ),
    [followUpOptions, selectedIndexes]
  );
  const selectedPrompt = React.useMemo(() => buildSuggestedPrompt(selectedOptions), [selectedOptions]);

  React.useEffect(() => {
    if (selectedPrompt) {
      onUsePromptSuggestion?.(selectedPrompt);
    }
  }, [onUsePromptSuggestion, selectedPrompt]);

  React.useEffect(() => {
    setSelectedIndexes([]);
  }, [followUpOptions]);

  const handleToggle = (index: number) => {
    if (disabled) {
      return;
    }

    setSelectedIndexes((currentIndexes) =>
      currentIndexes.includes(index) ? filter(currentIndexes, (value) => value !== index) : [...currentIndexes, index]
    );
  };

  return (
    <Stack
      spacing={1.25}
      sx={{
        mt: 1.5,
        p: 1.5,
        borderRadius: 1.5,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.04em' }}>
        Suggested next steps
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Select one or more options to seed the prompt composer, or ignore these and type your own prompt.
      </Typography>
      <Stack spacing={1}>
        {map(followUpOptions, (option, index) => {
          const checked = selectedIndexes.includes(index);

          return (
            <ButtonBase
              key={`${option.label}-${index}`}
              onClick={() => handleToggle(index)}
              disabled={disabled}
              sx={{
                width: '100%',
                textAlign: 'left',
                borderRadius: 1,
                border: `1px solid ${checked ? theme.palette.primary.main : alpha(theme.palette.divider, 0.9)}`,
                backgroundColor: checked ? alpha(theme.palette.primary.main, 0.1) : theme.palette.background.paper,
                px: 1.25,
                py: 0.9,
                justifyContent: 'flex-start',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'auto minmax(0, 1fr)',
                  columnGap: 1,
                  rowGap: 0.4,
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Checkbox checked={checked} tabIndex={-1} disableRipple disabled={disabled} sx={{ p: 0.25, m: 0 }} />
                <Typography variant="body2" sx={{ minWidth: 0, fontWeight: 600, color: 'text.primary' }}>
                  {option.label}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ gridColumn: '2 / 3', whiteSpace: 'pre-wrap' }}
                >
                  {option.prompt}
                </Typography>
              </Box>
            </ButtonBase>
          );
        })}
      </Stack>
      <Stack direction="row" justifyContent="flex-end">
        <Button
          size="small"
          variant="contained"
          endIcon={<ArrowUpwardRoundedIcon fontSize="small" />}
          disabled={disabled || !selectedPrompt}
          onClick={() => selectedPrompt && onSendPromptSuggestion?.(selectedPrompt)}
        >
          Send selected
        </Button>
      </Stack>
    </Stack>
  );
};

export const AssistantMessageContent: React.FC<{
  content: string;
  isStreaming?: boolean;
  streamingLabel?: string;
  showStreamingIndicator?: boolean;
  followUpOptions?: FollowUpOption[];
  onUsePromptSuggestion?: (prompt: string) => void;
  onSendPromptSuggestion?: (prompt: string) => void;
  promptSuggestionsDisabled?: boolean;
  studyId?: string;
}> = ({
  content,
  isStreaming,
  streamingLabel = DEFAULT_STREAMING_LABEL,
  showStreamingIndicator = true,
  followUpOptions = [],
  onUsePromptSuggestion,
  onSendPromptSuggestion,
  promptSuggestionsDisabled = false,
  studyId,
}) => {
  const hasContent = Boolean(content.trim());
  const resolvedContent = useEntityMergeTagResolver({ content, studyId });
  const hasFollowUpOptions = followUpOptions.length > 0;

  return (
    <Box sx={markdownContentSx}>
      {hasContent ? <Markdown options={markdownOverrides}>{resolvedContent}</Markdown> : null}
      {isStreaming && showStreamingIndicator ? (
        <StreamingResponseIndicator compact={hasContent} label={streamingLabel} />
      ) : null}
      {!isStreaming && hasFollowUpOptions ? (
        <FollowUpOptionsPanel
          followUpOptions={followUpOptions}
          onUsePromptSuggestion={onUsePromptSuggestion}
          onSendPromptSuggestion={onSendPromptSuggestion}
          disabled={promptSuggestionsDisabled}
        />
      ) : null}
    </Box>
  );
};
